import { useMemo, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { formatDate } from '@/shared/utils/format';
import type { AdsReportData } from '../types';
import {
  adsFlags,
  adsTake,
  callTiles,
  campaignBars,
  countedBy,
  funnelFor,
  funnelEndLabel,
  idleCampaigns,
  money,
  readyPlatforms,
  whole,
  count,
  withShortCampaignNames,
  type AdsMetric,
} from '../utils/adsReport';
import { snapshotView } from '../utils/adsSnapshot';
import { SnapshotHeader, SnapshotSections, ThisWeekStrip } from './JennySnapshotSections';
import styles from './JennyAdsReport.module.css';

const METRICS: Array<{ key: AdsMetric; label: string }> = [
  { key: 'spend', label: 'Spend' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'conversions', label: 'Conversions' },
];

/**
 * Jenny's weekly ads report, drawn from the figures of the performance read it cites: her take,
 * (1) where the money went, (2) from ad to patient, (3) what stood out, then the fine print and
 * her full written report folded away. Reporting only: nothing here changes the ad accounts.
 */
export function JennyAdsReport({
  report: fullNames,
  agentName,
  fullReport,
  writtenTake,
  onAsk,
}: {
  report: AdsReportData;
  agentName: string;
  /** Her written report, rendered by the caller, for the fold. */
  fullReport: ReactNode | null;
  /** Her own take from her written report: shown in place of the computed sentences on a snapshot report. */
  writtenTake?: string | null;
  /** Puts a question in the conversation's message box; without it, flags have no ask button. */
  onAsk?: (question: string) => void;
}) {
  const report = useMemo(() => withShortCampaignNames(fullNames), [fullNames]);
  // A report written from the location's ads snapshot carries its header, this week, the measured
  // sections and its own flags (facts with why); an older report has none of these.
  const view = useMemo(() => snapshotView(fullNames.snapshot), [fullNames.snapshot]);
  const [metric, setMetric] = useState<AdsMetric>('spend');
  const ready = readyPlatforms(report);
  const [platformKey, setPlatformKey] = useState(
    ready.find((p) => p.totals.spend > 0)?.platform ?? report.platforms[0]?.platform,
  );
  const [notesOpen, setNotesOpen] = useState(false);
  const [fullOpen, setFullOpen] = useState(false);
  const take = adsTake(report);
  if (!take) return null;
  // A report with calls leads with them: spent, calls of 30s or more, what each cost, missed.
  const tiles =
    (view?.calls.available &&
      callTiles({
        spend: view.totals30?.spend ?? null,
        costPerCallAtLeast30s: view.totals30?.costPerCallAtLeast30s ?? null,
        calls: view.calls.value.total,
        atLeast30s: view.calls.value.atLeast30s,
        missed: view.calls.value.missed,
      })) ||
    take.tiles;
  const bars = campaignBars(report, metric);
  const idle = idleCampaigns(report);
  const flags = view
    ? view.worthAttention.map((f) => ({
        title: f.fact,
        body: f.why ?? '',
        question: `Tell me more about this: ${f.fact}`,
      }))
    : adsFlags(report);
  const platform = report.platforms.find((p) => p.platform === platformKey) ?? report.platforms[0];
  const readyPlatform = ready.find((p) => p.platform === platform.platform);
  const until = report.windowEnd
    ? formatDate(new Date(`${report.windowEnd}T12:00:00Z`), 'MMM d')
    : formatDate(new Date(report.asOf), 'MMM d');

  return (
    <div className={styles.report}>
      {view && <SnapshotHeader view={view} />}
      <section className={styles.take} aria-label={`${agentName}'s take`}>
        <div className={styles.takeKicker}>{agentName}&apos;s take</div>
        <p className={styles.takeText}>{view && writtenTake ? writtenTake : take.sentences}</p>
        <div className={styles.tiles}>
          {tiles.map((tile) => (
            <div key={tile.label} className={styles.tile}>
              <div className={styles.tileLabel}>{tile.label}</div>
              <div className={styles.tileValue}>{tile.value}</div>
              <div className={styles.tileNote}>{tile.note}</div>
            </div>
          ))}
        </div>
      </section>

      {view && <ThisWeekStrip view={view} />}

      <section className={styles.section} aria-label="Where the money went">
        <div className={styles.sectionHead}>
          <StepTitle
            step={1}
            title="Where the money went"
            sub="By campaign. Click one for its numbers."
          />
          <div className={styles.segmented} role="group" aria-label="Measure">
            {METRICS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={clsx(styles.segment, metric === m.key && styles.segmentOn)}
                aria-pressed={metric === m.key}
                onClick={() => setMetric(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.box}>
          {bars.length === 0 && (
            <div className={styles.empty}>
              No campaign spent anything or was shown in this period.
            </div>
          )}
          {bars.map((bar) => (
            <CampaignBar key={bar.key} bar={bar} report={report} />
          ))}
          {idle.length > 0 && (
            <div className={styles.idle}>
              <strong>On, but not shown to anyone:</strong> {idle.join(', ')}. No spend and no
              impressions this period.
            </div>
          )}
        </div>
      </section>

      <section className={styles.section} aria-label="From ad to patient">
        <div className={styles.sectionHead}>
          <StepTitle
            step={2}
            title="From ad to patient"
            sub="Each step is its own number. The ad accounts can't see past the last one."
          />
          {/* A platform that is not in the report (Meta not connected) gets no tab: it would only say so. */}
          {ready.length > 1 && (
            <div className={styles.segmented} role="group" aria-label="Platform">
              {ready.map((p) => (
                <button
                  key={p.platform}
                  type="button"
                  className={clsx(
                    styles.segment,
                    platform.platform === p.platform && styles.segmentOn,
                  )}
                  aria-pressed={platform.platform === p.platform}
                  onClick={() => setPlatformKey(p.platform)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={styles.box}>
          {!readyPlatform ? (
            <div className={styles.empty}>
              {platform.label} isn&apos;t in this report
              {platform.reason ? `: ${platform.reason}` : '.'}
            </div>
          ) : readyPlatform.totals.spend === 0 && readyPlatform.totals.impressions === 0 ? (
            <div className={styles.empty}>
              No {countedBy(readyPlatform)} campaign spent anything in the last {report.days} days,
              so there&apos;s nothing to show for {countedBy(readyPlatform)} this period.
            </div>
          ) : (
            <div className={styles.funnel}>
              {funnelFor(readyPlatform).map((step) => (
                <div key={step.label} className={styles.funnelRow}>
                  <div>
                    <div className={styles.funnelLabel}>{step.label}</div>
                    <div className={styles.funnelNote}>{step.note}</div>
                  </div>
                  <div className={styles.funnelBarCell}>
                    <progress
                      className={styles.funnelBar}
                      max={100}
                      value={step.width}
                      aria-hidden="true"
                    />
                    <span className={styles.funnelValue}>{step.value}</span>
                  </div>
                </div>
              ))}
              <div className={styles.funnelRow}>
                <div>
                  <div className={styles.funnelLabelMuted}>{funnelEndLabel(readyPlatform)}</div>
                  <div className={styles.funnelNote}>not in the ad data</div>
                </div>
                <div className={styles.funnelEnd}>
                  The ad data stops here. Whether these became booked patients is something your
                  front desk or practice software can tell you; Google and Meta can&apos;t see it.
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {view && <SnapshotSections view={view} />}

      {flags.length > 0 && (
        <section className={styles.section} aria-label="What stood out">
          <StepTitle
            step={3}
            title={view ? 'Worth attention' : 'What stood out'}
            sub="Facts worth a look. This is a report: nothing here changes your ads. Changes come as their own task for you to approve."
          />
          <div className={styles.flags}>
            {flags.map((flag) => (
              <div key={flag.title} className={styles.flag}>
                <div className={styles.flagTitle}>{flag.title}</div>
                <div className={styles.flagBody}>{flag.body}</div>
                {onAsk && (
                  <div className={styles.flagAction}>
                    <Button variant="secondary" size="sm" onClick={() => onAsk(flag.question)}>
                      Ask {agentName} about this
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <section className={styles.fold}>
        <button
          type="button"
          className={styles.foldHeader}
          aria-expanded={notesOpen}
          onClick={() => setNotesOpen((v) => !v)}
        >
          <span className={styles.foldTitle}>
            How to read these numbers <span className={styles.optional}>Optional</span>
          </span>
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={clsx(styles.chevron, notesOpen && styles.chevronOpen)}
          />
        </button>
        {notesOpen && (
          <div className={styles.foldBody}>
            <p>
              <strong>Period.</strong> The last {report.days} days, up to {until}, from {agentName}
              &apos;s read of your ad accounts.
            </p>
            <p>
              <strong>Exact figures.</strong> Every number here is what Google or Meta reported, not
              an estimate.
            </p>
            <p>
              <strong>&ldquo;Conversions&rdquo; are each platform&apos;s own count.</strong> Google
              and Meta each decide what counts, under their own rules. A conversion is not the same
              as an answered call, a form lead or a booked patient, and the two platforms&apos;
              counts are never added together.
            </p>
            <p>
              <strong>Why there&apos;s no single &ldquo;return&rdquo; number.</strong> Clicks, calls
              and form leads are different things, so they aren&apos;t blended into one
              cost-per-result. The one number that adds up honestly is what you spent.
            </p>
            {fullReport && (
              <>
                <button
                  type="button"
                  className={styles.linkButton}
                  aria-expanded={fullOpen}
                  onClick={() => setFullOpen((v) => !v)}
                >
                  {fullOpen ? `Hide ${agentName}'s full report` : `Read ${agentName}'s full report`}
                </button>
                {fullOpen && <div className={styles.fullReport}>{fullReport}</div>}
              </>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function StepTitle({ step, title, sub }: { step: number; title: string; sub: string }) {
  return (
    <div className={styles.stepTitleRow}>
      <span className={styles.stepNumber} aria-hidden="true">
        {step}
      </span>
      <div>
        <h3 className={styles.stepTitle}>{title}</h3>
        <p className={styles.stepSub}>{sub}</p>
      </div>
    </div>
  );
}

function CampaignBar({
  bar,
  report,
}: {
  bar: ReturnType<typeof campaignBars>[number];
  report: AdsReportData;
}) {
  const [open, setOpen] = useState(false);
  const campaign = readyPlatforms(report)
    .flatMap((p) =>
      p.campaigns.map((c, i) => ({ c, key: `${p.platform}:${i}:${c.name}`, by: countedBy(p) })),
    )
    .find((x) => x.key === bar.key);
  return (
    <div>
      <button
        type="button"
        className={styles.bar}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.barHead}>
          <span>
            <span className={styles.barName}>{bar.name}</span>
            {bar.detail && <span className={styles.barDetail}> · {bar.detail}</span>}
          </span>
          <span className={styles.barValue}>
            {bar.value} <span className={styles.barShare}>{bar.share}</span>
          </span>
        </span>
        <progress className={styles.barTrack} max={100} value={bar.width} aria-hidden="true" />
      </button>
      {open && campaign && (
        <dl className={styles.barDetails}>
          <div>
            <dt>{money(campaign.c.spend)}</dt>
            <dd>spent</dd>
          </div>
          <div>
            <dt>{whole(campaign.c.impressions)}</dt>
            <dd>times shown</dd>
          </div>
          <div>
            <dt>{whole(campaign.c.clicks)}</dt>
            <dd>clicks</dd>
          </div>
          <div>
            <dt>{count(campaign.c.conversions)}</dt>
            <dd>{campaign.by}-counted conversions</dd>
          </div>
          <div>
            <dt>
              {campaign.c.conversions > 0
                ? money(campaign.c.spend / campaign.c.conversions)
                : 'None'}
            </dt>
            <dd>per conversion ({campaign.by}&apos;s figure)</dd>
          </div>
        </dl>
      )}
    </div>
  );
}
